import { z } from "zod"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { userHasPermission } from "../../../utils"
import { NoPermission } from "../../../components/UI/no-permission"
import { ChangeEvent, lazy, Suspense, useEffect, useMemo, useState, type KeyboardEvent } from "react"
import TabsList from "../../../components/Common/TabsList"
import { FormsContactOrderBy } from "../../../client"
import { useQuery } from "@tanstack/react-query"
import { Center, Flex, SkeletonText, Spinner, Table, useDisclosure } from "@chakra-ui/react"
import Sort from "../../../components/Common/Sort"
import ActionsMenu from "../../../components/Common/ActionsMenu"
import { PaginationFooter } from "../../../components/Common/PaginationFooter"
import SearchField from "../../../components/Common/SearchField"
import { readFormsContacts } from "../../../api/contacts"

const DetailContact = lazy(() => import("../../../components/Forms/DetailContact"))
const PER_PAGE = 25

const contactSearchSchema = z.object({
  page: z.number().catch(1),
})

export const Route = createFileRoute("/_layout/forms/")({
  component: Forms,
  validateSearch: (search) => contactSearchSchema.parse(search),
})


function ContactTable() {
  // Permission check
  const [fo_read, fo_delete] = useMemo(() => {
    return ["read", "delete"].map(
      (action) => userHasPermission([{ perm: `form_contact_${action}`, is_allowed: false }])[0].is_allowed
    )
  }, [userHasPermission])

  if(!fo_read) return <NoPermission/>

  const fields = [
    { title: "Nachname", col: "lastname", align: undefined },
    { title: "Vorname", col: "firstname", align: undefined },
    { title: "E-Mail", col: "email", align: undefined },
    { title: "Erstellungsdatum", col: "created_at", align: undefined },
    { title: "Id", col: null, align: undefined },
    { title: "Aktionen", col: null, align: "right" as const },
  ]

  const [inputValue, setInputValue] = useState<string>("")
  const [searchValue, setSearchValue] = useState<string>("")

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (inputValue != null) {
        setSearchValue(inputValue)
      }
    }
  }

  const handleOnClick = () => {
    if (inputValue != null) {
      setSearchValue(inputValue)
    }
  }

  const handleClear = () => {
    setInputValue("")
    if (inputValue != null) {
        setSearchValue("")
    }
  }

  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const setPage = (page: number) => navigate({
    search: (prev: { [key: string]: string }) => ({ ...prev, page }),
  })

  // Set page on search value change
  const [prevPage, setPrevPage] = useState<number>(page)
  useEffect(() => {
    if(searchValue == "") {
      setPage(prevPage)
    } else {
      setPrevPage(page)
      setPage(1)
    }
  }, [searchValue])

  // Sorting
  const [columnName, setColumnName] = useState<FormsContactOrderBy>()
  const [orderBy, setOrderBy] = useState<string>("")
  const [isDesc, setIsDesc] = useState<boolean>()

  // Query
  const queryOptions = useMemo(() =>
    readFormsContacts({
      per_page: PER_PAGE,
      page: page,
      order_by: columnName,
      descending: isDesc,
      query: searchValue,
    }),
  [page, columnName, isDesc, searchValue])

  const {
    data: contacts,
    isPending,
    isPlaceholderData,
  } = useQuery({
    ...queryOptions,
    placeholderData: (prevData) => prevData,
  })

  // Sorting
  const sortTable = (name: FormsContactOrderBy) => {
    if (columnName !== name) {
      setColumnName(name)
      setOrderBy("")
      sorting("")
    } else {
      sorting(orderBy)
    }
  }

  const sorting = (order: string) => {
    switch (order) {
      case "":
        setOrderBy("desc")
        setIsDesc(true)
        break
      case "desc":
        setOrderBy("asc")
        setIsDesc(false)
        break
      case "asc":
        setOrderBy("")
        setIsDesc(undefined)
        setColumnName(undefined)
        break
    }
  }

  // Open forms-contact detail view
  const detailModal = useDisclosure()
  const [contactId, setContactId] = useState("")

  const openModal = (id: string) => {
    setContactId(id)
    detailModal.onOpen()
  }

  return (
    <>
      <Flex py={8} justify={{base: "flex-start",md: "flex-end"}}>
        <SearchField
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={handleOnClick}
          onClear={handleClear}
        />
      </Flex>

      {detailModal.open &&
        <Suspense fallback={
          <Center
            bg="rgba(0, 0, 0, 0.3)"
            position="fixed"
            top="0"
            left="0"
            width="100vw"
            height="100vh"
            zIndex="10000"
          >
            <Spinner size="lg" color="white" />
          </Center>}
        >
          <DetailContact
            isOpen={detailModal.open}
            onClose={detailModal.onClose}
            contactId={contactId}
          />
        </Suspense>
      }

      <Table.ScrollArea asChild>
        <Table.Root size={{ base: "sm", md: "md" }} whiteSpace="normal" display={{md: "table"}}>
          <Table.Header>
            <Table.Row>
              {fields.map((field, i) =>
                field.col != null ? (
                  <Table.ColumnHeader
                    key={i}
                    onClick={() => sortTable(field.col as FormsContactOrderBy)}
                    cursor="pointer"
                    textAlign={field.align}
                  >
                    {field.title}
                    {columnName === field.col &&
                      <Sort
                        columnNamePassed={columnName}
                        orderByPassed={orderBy}
                      />
                    }
                  </Table.ColumnHeader>
                ) : (
                  <Table.ColumnHeader key={field.title} textAlign={field.align}>
                    {field.title}
                  </Table.ColumnHeader>
                )
              )}
            </Table.Row>
          </Table.Header>
          {isPending ? (
            <Table.Body>
              <Table.Row>
                {new Array(6).fill(null).map((_, index) => (
                  <Table.Cell key={index}>
                    <SkeletonText noOfLines={1} paddingBlock="16px" />
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          ) : (
            <Table.Body>
              {!contacts?.data ? (
                <Table.Row>
                  <Table.Cell colSpan={6} textAlign="center">
                    Keine Kontaktanfragen verfügbar
                  </Table.Cell>
                </Table.Row>
              ) : (
                contacts?.data.map((contact, i) => (
                  <Table.Row key={i} opacity={isPlaceholderData ? 0.5 : 1}>
                    <Table.Cell onClick={() => openModal(contact.id)} cursor="pointer">
                      {contact.lastname}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(contact.id)} cursor="pointer">
                      {contact.firstname}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(contact.id)} cursor="pointer">
                      {contact.email}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(contact.id)} cursor="pointer">
                      {new Date(`${contact.created_at}`).toLocaleString(
                        "de-CH",
                        { timeZone: "Europe/Zurich" },
                      )}
                    </Table.Cell>

                    <Table.Cell onClick={() => openModal(contact.id)} cursor="pointer">
                      {contact.id}
                    </Table.Cell>

                    <Table.Cell textAlign="right">
                      <ActionsMenu
                        type="Kontakt"
                        value={contact}
                        disableDelete={!fo_delete}
                        hideEdit={true}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          )}
        </Table.Root>
      </Table.ScrollArea>
      <PaginationFooter
        onChangePage={setPage}
        page={page}
        count={contacts?.count ? contacts?.count : 0}
        perPage={PER_PAGE}
      />
    </>
  )
}

export default ContactTable

function Forms() {
  const tabs = useMemo(() => [
    { value: "forms", title: "Kontaktanfragen" },
    { value: "forms/subscriptions", title: "Abo-Bestellungen" },
    { value: "forms/newsletters", title: "Newsletter" },
  ], [])

  return <TabsList tabs={tabs} defaultVal="forms" />
}
